interface RadioOption {
  label: string | number;
  value: string | number;
  globalStateName?: 'setting';
}

interface SliderSubTitle {
  globalStateName: 'value';
  title: string;
  isNotRadio: true;
  data: [number, number];
  sliderLabel: string;
}

interface RadioSubTitle {
  title: string;
  globalStateName: 'setting' | 'value' | 'audioSpeedSetting';
  isNotRadio?: false;
  data: RadioOption[];
}

type SubTitle = RadioSubTitle | SliderSubTitle;

interface VariableTitleEntry {
  title: string;
  data: number[];
  globalStateName: 'value';
}

type WritingVariableTitles = {
  full: VariableTitleEntry;
  anagram: VariableTitleEntry;
  gap: VariableTitleEntry;
};

type ComprehensionVariableTitles = {
  open: VariableTitleEntry;
  closed: VariableTitleEntry;
};

interface ExerciseWithWritingVariants {
  title: string;
  subTitles: SubTitle[];
  variableTitles: WritingVariableTitles;
}

interface ExerciseWithComprehensionVariants {
  title: string;
  subTitles: SubTitle[];
  variableTitles: ComprehensionVariableTitles;
}

interface ExerciseWithoutVariants {
  title: string;
  subTitles: SubTitle[];
  variableTitles?: never;
}

type WordsetExercise = ExerciseWithWritingVariants | ExerciseWithoutVariants;

type SentencesetExercise =
  | ExerciseWithWritingVariants
  | ExerciseWithoutVariants;

type TextsetExercise =
  | ExerciseWithComprehensionVariants
  | ExerciseWithoutVariants;

interface Settings {
  wordset: WordsetExercise[];
  sentenceset: SentencesetExercise[];
  textset: TextsetExercise[];
}

export const settings: Settings = {
  wordset: [
    {
      title: 'Schriftliches Benennen',
      subTitles: [
        {
          title: 'Aufgabentyp',
          globalStateName: 'setting',
          data: [
            { label: 'Lückenwort', value: 'gap' },
            { label: 'Anagramme', value: 'anagram' },
            { label: 'Ganze Tastatur', value: 'full' },
          ],
        },
      ],
      variableTitles: {
        full: { title: '', data: [], globalStateName: 'value' },
        anagram: {
          title: 'Anzahl Zusatzgrapheme',
          data: [0, 1, 2, 3, 4],
          globalStateName: 'value',
        },
        gap: {
          title: 'Anzahl Lücken',
          data: [1, 2, 3, 4],
          globalStateName: 'value',
        },
      },
    },
    {
      title: 'Mündliches Benennen',
      subTitles: [
        {
          globalStateName: 'setting',
          title: '',
          data: [
            {
              label: 'Mit Audioaufnahme',
              globalStateName: 'setting',
              value: 'record',
            },
            {
              label: 'Ohne Audioaufnahme',
              globalStateName: 'setting',
              value: 'norecord',
            },
          ],
        },
        {
          globalStateName: 'value',
          title: 'Aufnahmedauer',
          isNotRadio: true,
          data: [5, 20],
          sliderLabel: 'Sekunden',
        },
      ],
    },
    {
      title: 'Auditives Sprachverständnis',
      subTitles: [
        {
          title: 'Ablenker',
          globalStateName: 'setting',
          data: [
            { label: 'Unrelatiert', value: 'unrelated' },
            { label: 'Semantisch', value: 'related' },
            { label: 'Aus Set', value: 'fromset' },
          ],
        },
        {
          title: 'Anzahl Bilder',
          globalStateName: 'value',
          data: [
            { label: 2, value: 2 },
            { label: 3, value: 3 },
            { label: 4, value: 4 },
          ],
        },
      ],
    },
    {
      title: 'Lesesinnverständnis',
      subTitles: [
        {
          title: 'Ablenker',
          globalStateName: 'setting',
          data: [
            { label: 'Unrelatiert', value: 'unrelated' },
            { label: 'Semantisch', value: 'related' },
            { label: 'Aus Set', value: 'fromset' },
          ],
        },
        {
          title: 'Anzahl Bilder',
          globalStateName: 'value',
          data: [
            { label: 2, value: 2 },
            { label: 3, value: 3 },
            { label: 4, value: 4 },
          ],
        },
      ],
    },
  ],
  sentenceset: [
    {
      title: 'Schriftliches Benennen',
      subTitles: [
        {
          title: 'Aufgabentyp',
          globalStateName: 'setting',
          data: [
            { label: 'Lückensatz', value: 'gap' },
            { label: 'Anagramme', value: 'anagram' },
            { label: 'Ganze Tastatur', value: 'full' },
          ],
        },
      ],
      variableTitles: {
        full: { title: '', data: [], globalStateName: 'value' },
        anagram: {
          title: 'Anzahl Zusatzwörter',
          data: [0, 1, 2, 3, 4],
          globalStateName: 'value',
        },
        gap: {
          title: 'Anzahl Lücken',
          data: [1, 2, 3, 4],
          globalStateName: 'value',
        },
      },
    },
    {
      title: 'Mündliches Benennen',
      subTitles: [
        {
          globalStateName: 'setting',
          title: '',
          data: [
            {
              label: 'Mit Audioaufnahme',
              globalStateName: 'setting',
              value: 'record',
            },
            {
              label: 'Ohne Audioaufnahme',
              globalStateName: 'setting',
              value: 'norecord',
            },
          ],
        },
        {
          globalStateName: 'value',
          title: 'Aufnahmedauer',
          isNotRadio: true,
          data: [5, 20],
          sliderLabel: 'Sekunden',
        },
      ],
    },
    {
      title: 'Auditives Sprachverständnis',
      subTitles: [
        {
          title: 'Ablenker',
          globalStateName: 'setting',
          data: [
            { label: 'Unrelatiert', value: 'unrelated' },
            { label: 'Semantisch', value: 'related' },
            { label: 'Aus Set', value: 'fromset' },
          ],
        },
        {
          title: 'Anzahl Bilder',
          globalStateName: 'value',
          data: [
            { label: 2, value: 2 },
            { label: 3, value: 3 },
            { label: 4, value: 4 },
          ],
        },
      ],
    },
    {
      title: 'Lesesinnverständnis',
      subTitles: [
        {
          title: 'Ablenker',
          globalStateName: 'setting',
          data: [
            { label: 'Unrelatiert', value: 'unrelated' },
            { label: 'Semantisch', value: 'related' },
            { label: 'Aus Set', value: 'fromset' },
          ],
        },
        {
          title: 'Anzahl Bilder',
          globalStateName: 'value',
          data: [
            { label: 2, value: 2 },
            { label: 3, value: 3 },
            { label: 4, value: 4 },
          ],
        },
      ],
    },
  ],
  textset: [
    {
      title: 'Schreiben',
      subTitles: [
        {
          title: '',
          globalStateName: 'setting',
          data: [
            { label: 'Lückentext', value: 'gap' },
            { label: 'Diktat schreiben', value: 'diktat' },
          ],
        },
      ],
    },
    {
      title: 'Mündliches Nacherzählen',
      subTitles: [
        {
          globalStateName: 'setting',
          title: '',
          data: [
            {
              label: 'W-Fragen geleitet',
              globalStateName: 'setting',
              value: 'questions',
            },
          ],
        },
      ],
    },
    {
      title: 'Auditives Sprachverständnis',
      subTitles: [
        {
          title: 'Audiogeschwindigkeit',
          globalStateName: 'audioSpeedSetting',
          data: [
            { label: 'Normal', value: 'normal' },
            { label: 'Langsam', value: 'slow' },
          ],
        },
        {
          title: 'Art der Fragen',
          globalStateName: 'setting',
          data: [
            { label: 'Geschlossen', value: 'closed' },
            { label: 'Offen', value: 'open' },
          ],
        },
      ],
      variableTitles: {
        open: { title: '', data: [], globalStateName: 'value' },
        closed: {
          title: 'Anzahl der Antwortmöglichkeiten',
          data: [2, 3, 4],
          globalStateName: 'value',
        },
      },
    },
    {
      title: 'Lesesinnverständnis',
      subTitles: [
        {
          title: 'Art der Fragen',
          globalStateName: 'setting',
          data: [
            { label: 'Geschlossen', value: 'closed' },
            { label: 'Offen', value: 'open' },
          ],
        },
      ],
      variableTitles: {
        open: { title: '', data: [], globalStateName: 'value' },
        closed: {
          title: 'Anzahl der Antwortmöglichkeiten',
          data: [2, 3, 4],
          globalStateName: 'value',
        },
      },
    },
  ],
};
